import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useRef,
} from "react";

import { Box, LinearProgress } from "@mui/material";

// Create context for loading state
const LoadingContext = createContext({
  isLoading: false,
  progress: 0,
  startLoading: () => {},
  stopLoading: () => {},
});

// Provider component
export const LoadingProvider = ({ children }: { children: ReactNode }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<NodeJS.Timer | null>(null);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    setProgress(0);

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Start new progress timer
    timerRef.current = setInterval(() => {
      setProgress((oldProgress) => {
        if (oldProgress >= 90) {
          // Slow down near the end to avoid reaching 100
          const diff = Math.random() * 0.5;
          return Math.min(oldProgress + diff, 90);
        }
        const diff = Math.random() * 10;
        return Math.min(oldProgress + diff, 90);
      });
    }, 500);
  }, []);

  const stopLoading = useCallback(() => {
    // Clear the interval first
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Jump to 100%
    setProgress(100);

    // Reset after a small delay
    setTimeout(() => {
      setIsLoading(false);
      setProgress(0);
    }, 200);
  }, []);

  return (
    <LoadingContext.Provider
      value={{ isLoading, progress, startLoading, stopLoading }}
    >
      {children}
    </LoadingContext.Provider>
  );
};

// Custom hook to use loading state
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error("useLoading must be used within a LoadingProvider");
  }
  return context;
};

// Progress bar component
export const LoadingProgress = () => {
  const { isLoading, progress } = useLoading();

  return (
    <>
      {isLoading && (
        <Box
          sx={{
            width: "100%",
            position: "fixed",
            zIndex: 20,
            height: "100vh",
            backgroundColor: "rgba(255, 255, 255, 0.5)",
          }}
        >
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}
    </>
  );
};
